import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { ControlErrorDirective } from '../../../directive/control-error.directive';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { ForeignBankAccountFormModel } from './form-group/foreign-bank-account-form.model';
import { RequiredLabelDirective } from '../../../directive/required-label.directive';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, map, startWith } from 'rxjs';
import { SlownikRodzaj } from '../../../enums/slownik-rodzaj.enum';
import { Slownik } from '../../../models/slownik';
import { SlownikiApi } from '../../../services/api/slowniki.api';
import { HttpResponseService } from '../../../../core/services/http-error-response.service';

@Component({
  selector: 'app-form-foreign-bank-account',
  imports: [
    ControlErrorDirective,
    FormsModule,
    InputText,
    ReactiveFormsModule,
    RequiredLabelDirective,
  ],
  templateUrl: './form-foreign-bank-account.component.html',
  styleUrl: './form-foreign-bank-account.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormForeignBankAccountComponent {
  public formGroup = input.required<FormGroup<ForeignBankAccountFormModel>>();
  readonly #slownikiApi = inject(SlownikiApi);
  readonly #httpResponseService = inject(HttpResponseService);
  readonly #destroyRef = inject(DestroyRef);
  protected readonly numerKonta = signal<string | null>(null);
  protected readonly panstwaUEEFTA = toSignal(
    this.#slownikiApi.getSlownik$(SlownikRodzaj.PANSTWA_SWR).pipe(
      map((resp: Slownik[]) =>
        resp.map(
          (value) =>
            ({
              id: value.id,
              nazwa: value.panstwo,
              kod: value.symbol,
              kodOrganizacji: value.kodOrganizacji,
              symbol: value.symbol,
            }) as Slownik,
        ),
      ),
      catchError((err) => {
        this.#httpResponseService.showErrorMessage(
          err,
          'Wystąpił błąd podczas pobrania danych do walidacji.',
        );
        return EMPTY;
      }),
      takeUntilDestroyed(this.#destroyRef),
    ),
    { initialValue: [] },
  );

  constructor() {
    effect(() => {
      const fg = this.formGroup();

      fg.controls.numerKonta.valueChanges
        .pipe(
          startWith(fg.controls.numerKonta.value),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe((value) => {
          this.numerKonta.set(value);
        });
    });
  }

  protected readonly isAccountFromUEEFTA = computed(() => {
    const accountNumber = this.numerKonta();
    const panstwa = this.panstwaUEEFTA();

    const charsInNumber = accountNumber
      ? accountNumber.match(/[a-zA-Z]+/g)
      : null;

    if (charsInNumber?.length && panstwa?.length) {
      const countrySymbol = charsInNumber[0];

      return !panstwa.some(
        (panstwo) =>
          panstwo.kod === countrySymbol &&
          (panstwo.kodOrganizacji === 'UE' ||
            panstwo.kodOrganizacji === 'EFTA' ||
            panstwo.symbol === 'GB') &&
          panstwo.symbol !== 'PL',
      );
    }
    return false;
  });
}
